/* Mattress Finder — guided quiz for sections/mattress-finder.liquid.

   Products come from templates/collection.finder-data.liquid (JSON) and are
   scored against the shopper's answers. The scoring part is pure and exposed as
   window.MattressFinderScore so it can be tested outside the page.

   Answers (kept in sessionStorage so a refresh keeps your place):
     people   'one' | 'two'
     firmness 'soft' | 'medium' | 'firm' | 'unsure'
     type     'foam' | 'springs' | 'hybrid' | 'any'
     position 'side' | 'back' | 'front' | 'mixed'
     temp     0..4  (very cool → very hot)
     weight   null (prefer not to say) | [stone] | [stone, stone]
     prefs    ['ortho' | 'pressure' | 'noroll' | 'edge' | 'natural' | 'hypo']
     budget   'low' | 'mid' | 'high' | 'any'
     size     variant size name, e.g. 'Kingsize'
*/
(function () {
  'use strict';

  /* ======================= Scoring ======================= */

  const FIRMNESS_LABELS = ['', 'Super Soft', 'Soft', 'Soft - Medium', 'Medium', 'Medium Firm', 'Firm', 'Super Firm'];
  const FIRMNESS_RULES = [
    [/super\s*soft|extra\s*soft/, 1],
    [/soft\s*-?\s*(to\s*)?medium|medium\s*-?\s*soft/, 3],
    [/super\s*firm|extra\s*firm|ultimate\s*firm/, 7],
    [/medium\s*-?\s*firm/, 5],
    [/medium/, 4],
    [/firm/, 6],
    [/soft/, 2],
  ];
  const FIRMNESS_COLLECTIONS = { softer: 2, medium: 4, 'medium-firm': 5, firm: 6, 'super-firm': 7 };

  // Order matters: longer names first so "small double" doesn't match "double".
  const SIZE_RULES = [
    ['small single', 'small single'], ['small double', 'small double'],
    ['superking', 'superking'], ['super king', 'superking'],
    ['kingsize', 'kingsize'], ['king size', 'kingsize'], ['king', 'kingsize'],
    ['double', 'double'], ['single', 'single'],
  ];

  const FIRM_TARGET = { soft: 2.6, medium: 4.3, firm: 5.9 };
  const POSITION_TARGET = { side: 3.6, back: 4.8, front: 5.6, mixed: 4.4 };
  const STONE_KG = 6.35;
  // Reasons-to-buy that describe the offer rather than the mattress itself.
  const PROMO_REASON = /pillow|deliver|trial|warrant|guarantee|free|finance|recycl|removal|made in|british/i;
  // Labels of the custom.quiz_features metafield (scripts/17-import-quiz-features.mjs).
  const QUIZ_FEATURE_KEYS = {
    cooling: 'Cooling', ortho: 'Orthopaedic', pressure: 'Pressure relief', noroll: 'Low partner disturbance',
    edge: 'Edge support', natural: 'Natural fillings', hypo: 'Hypoallergenic',
  };
  const PREF_LABELS = {
    ortho: 'Orthopaedic support', pressure: 'Pressure relief', noroll: 'No roll together',
    edge: 'Edge-to-edge support', natural: 'Natural fillings', hypo: 'Hypoallergenic',
  };

  function firmnessValue(text) {
    const t = String(text || '').toLowerCase();
    for (const [re, v] of FIRMNESS_RULES) if (re.test(t)) return v;
    return 0;
  }

  function sizeKey(text) {
    const t = String(text || '').toLowerCase().replace(/[’'"]/g, ' ').replace(/\s+/g, ' ');
    for (const [needle, key] of SIZE_RULES) if (t.includes(needle)) return key;
    return t.trim();
  }

  function eligible(p) {
    const title = String(p.title || '').trim();
    if (!/mattress$/i.test(title) || /removal|recycl/i.test(title)) return false;
    const tags = (p.tags || []).map((t) => String(t).toLowerCase());
    if (tags.includes('finder-exclude') || tags.includes('configurator-addon') || tags.includes('hidden-from-search')) return false;
    return !/add-on/i.test(p.productType || '');
  }

  function derive(p) {
    const title = String(p.title || '').toLowerCase();
    const cols = new Set(p.collections || []);
    const tags = (p.tags || []).map((t) => String(t).toLowerCase());
    const reasons = (p.reasons || []).filter((r) => r && r.title);
    const productReasons = reasons.filter((r) => !PROMO_REASON.test(r.title));
    const offerReasons = reasons.filter((r) => PROMO_REASON.test(r.title));
    const meta = [p.mattressType, p.features, tags.join(' ')].join(' ').toLowerCase();
    const all = [title, meta, productReasons.map((r) => `${r.title} ${r.text || ''}`).join(' ').toLowerCase()].join(' ');

    const springsInName = /pocket|spring|coil|sprung/.test(`${title} ${meta}`) || cols.has('pocket-spring') || cols.has('open-coil');
    const foamInName = /memory|foam|gel|latex|reflex/.test(title) || cols.has('memory-foam') || cols.has('latex');
    let type = 'springs';
    if (/hybrid/.test(title) || cols.has('hybrid') || (springsInName && foamInName)) type = 'hybrid';
    else if (!springsInName && foamInName) type = 'foam';

    const pocket = /pocket/.test(all) || cols.has('pocket-spring');
    const openCoil = /open coil|bonnell|traditional spring/.test(all) || cols.has('open-coil');

    let springCount = 0;
    const n = title.match(/\b(\d{3,4})\b/);
    if (n && +n[1] >= 300) springCount = +n[1];
    if (!springCount && p.springRange) {
      const r = String(p.springRange).match(/\d+/g);
      if (r) springCount = Math.max(...r.map(Number));
    }

    let firm = firmnessValue(p.firmness) || firmnessValue(title);
    if (!firm) for (const c of cols) if (FIRMNESS_COLLECTIONS[c]) { firm = FIRMNESS_COLLECTIONS[c]; break; }
    const firmKnown = Boolean(firm);
    if (!firm) firm = 4;

    // Automatic guess, used when the product's "Quiz features" metafield is empty.
    let f = {
      cooling: /cool|\bgel\b|\bice\b|copper|graphite|breathab|airflow|climate|tencel|bamboo/.test(all) || cols.has('gel'),
      ortho: /orthop/.test(all) || cols.has('orthopaedic') || firm >= 6,
      pressure: type !== 'springs' || /pillow\s*-?top|memory|latex|reflex|pressure/.test(all),
      noroll: (pocket || type === 'foam') && !(openCoil && !pocket),
      edge: /encapsulat|edge/.test(all),
      natural: /natural|wool|cotton|cashmere|silk|tencel|bamboo|latex|hemp|organic|mohair|alpaca/.test(all) || cols.has('natural'),
      hypo: /hypo.?allergenic|anti.?allerg|allerg|dust mite|anti.?bacterial|antimicrobial|aloe|silver|bamboo|tencel/.test(all),
    };
    const ticked = Array.isArray(p.quizFeatures) ? p.quizFeatures : [];
    const fromMetafield = ticked.length > 0;
    if (fromMetafield) {
      f = Object.fromEntries(Object.entries(QUIZ_FEATURE_KEYS).map(([key, label]) => [key, ticked.includes(label)]));
    }

    // Feature bullets: the product's own reasons-to-buy first, then facts we derived.
    const bullets = [];
    const pushBullet = (b) => {
      if (b && bullets.length < 5 && !bullets.some((x) => x.toLowerCase() === b.toLowerCase())) bullets.push(b);
    };
    productReasons.filter((r) => !firmnessValue(r.title) || /support|relief/i.test(r.title)).forEach((r) => pushBullet(r.title));
    if (pocket && springCount && !bullets.some((x) => /spring/i.test(x))) pushBullet(`${springCount} Pocket Springs`);
    else if (openCoil) pushBullet('Open Coil Springs');
    if (type !== 'springs') pushBullet(/gel/.test(title) ? 'Cool Gel Comfort Layer' : 'Memory Foam Comfort Layer');
    if (f.edge) pushBullet('Edge Support');
    if (/orthop/.test(all)) pushBullet('Orthopaedic Support');
    offerReasons.forEach((r) => pushBullet(r.title));

    const variants = {};
    (p.variants || []).forEach((v) => {
      const key = sizeKey(v.size);
      if (!variants[key] || (!variants[key].available && v.available)) variants[key] = v;
    });

    return {
      id: p.id, handle: p.handle, title: p.title, url: p.url, image: p.image, imageAlt: p.imageAlt,
      images: Array.isArray(p.images) ? p.images.filter(Boolean) : [], brandLogo: p.brandLogo || null, badge: p.badge || null,
      brandKey: title.split(/\s+/)[0],
      type, firm, firmKnown, firmLabel: p.firmness || FIRMNESS_LABELS[firm], springCount, f, fromMetafield, bullets, variants,
      rating: Number(p.rating) || 0,
      boost: tags.includes('finder-boost'),
    };
  }

  function weights(a) {
    return Array.isArray(a.weight) && a.weight.length ? a.weight.map((st) => st * STONE_KG) : [];
  }

  function targetFirmness(a) {
    const fromFeel = FIRM_TARGET[a.firmness];
    const fromPosition = POSITION_TARGET[a.position];
    let t = fromFeel && fromPosition ? fromFeel * 0.75 + fromPosition * 0.25 : fromFeel || fromPosition || 4.5;
    const kg = weights(a);
    if (kg.length) {
      const avg = kg.reduce((s, x) => s + x, 0) / kg.length;
      t += avg < 57 ? -0.5 : avg < 95 ? 0 : avg < 120 ? 0.6 : 1.1;
    }
    return Math.min(7, Math.max(1, t));
  }

  function score(d, a) {
    let got = 0;
    let max = 0;
    const why = [];
    const prefWhy = [];
    const part = (fraction, points) => { got += fraction * points; max += points; };

    // Feel (35): distance from the firmness we're aiming for.
    const target = targetFirmness(a);
    const dist = Math.abs(d.firm - target);
    part(Math.max(0, 1 - dist / 2.5), 35);
    const feelWhy = dist <= 0.75 && a.firmness && a.firmness !== 'unsure' ? [`The ${d.firmLabel.toLowerCase()} feel you asked for`] : [];

    // Construction (20).
    if (a.type && a.type !== 'any') {
      const match = d.type === a.type ? 1 : d.type === 'hybrid' || a.type === 'hybrid' ? 0.5 : 0;
      part(match, 20);
    } else part(1, 20);

    // Sleeping position (10).
    const pos = {
      side: [d.f.pressure ? 1 : 0.4, 'Cushions shoulders and hips for side sleeping'],
      back: [d.f.ortho || (d.firm >= 4 && d.firm <= 6) ? 1 : 0.5, 'Keeps your spine aligned when you sleep on your back'],
      front: [d.firm >= 5 ? 1 : d.firm >= 4 ? 0.6 : 0.2, 'Firm enough to stop your hips sinking on your front'],
      mixed: [d.type !== 'foam' ? 1 : 0.6, 'Responsive feel that makes it easy to change position'],
    }[a.position];
    if (pos) { part(pos[0], 10); if (pos[0] === 1) why.push(pos[1]); } else part(1, 10);

    // Temperature (5–15): warmer sleepers weight breathability more heavily.
    const temp = Number.isInteger(a.temp) ? a.temp : 2;
    const breathable = d.f.cooling ? 1 : d.type === 'springs' ? 0.7 : d.type === 'hybrid' ? 0.5 : 0.15;
    if (temp >= 3) {
      part(breathable, temp === 4 ? 15 : 10);
      if (d.f.cooling) why.push('Cooling design for warm sleepers');
    } else if (temp <= 1) part(d.f.pressure ? 1 : 0.6, 5);
    else part(1, 5);

    // Sharing (10): motion isolation matters for couples.
    if (a.people === 'two') {
      part(Math.min(1, (d.f.noroll ? 0.8 : 0.3) + (d.f.edge ? 0.2 : 0)), 10);
      if (d.f.noroll) why.push("You won't feel your partner moving");
    } else part(1, 10);

    // Body weight (10): heavier sleepers need deeper support.
    const kg = weights(a);
    if (kg.length) {
      const heaviest = Math.max(...kg);
      if (heaviest >= 100) {
        const fit = d.type !== 'foam' && (d.springCount >= 1000 || d.firm >= 5) ? 1 : d.type !== 'foam' ? 0.6 : 0.3;
        part(fit, 10);
        if (fit === 1) why.push('Extra support for heavier bodies');
      } else if (heaviest < 57) part(d.firm <= 4.5 ? 1 : 0.5, 10);
      else part(1, 10);
    } else part(1, 10);

    // Preferences (12 each).
    (a.prefs || []).forEach((key) => {
      const has = Boolean(d.f[key]);
      part(has ? 1 : 0, 12);
      if (has) prefWhy.push(PREF_LABELS[key]);
    });

    // Most personal reasons first: the feel they asked for, then their preferences.
    return { pct: Math.round((got / max) * 100), target, why: [...new Set([...feelWhy, ...prefWhy, ...why])].slice(0, 3) };
  }

  function withinBudget(price, budget, opts) {
    if (budget === 'low') return price <= opts.low;
    if (budget === 'mid') return price <= opts.high;
    if (budget === 'high') return price >= opts.high;
    return true;
  }

  function recommend(products, a, opts) {
    const size = sizeKey(a.size);
    const pool = [];
    products.forEach((p) => {
      if (!eligible(p)) return;
      const d = derive(p);
      const v = d.variants[size];
      if (!v || !v.available) return;
      const s = score(d, a);
      pool.push({ d, v, ...s, rank: s.pct + (d.boost ? 2 : 0) + d.rating * 0.3 });
    });
    pool.sort((x, y) => y.rank - x.rank);

    const inBudget = pool.filter((r) => withinBudget(r.v.price, a.budget, opts));
    const count = opts.count || 6;

    // Keep the list varied: at most 2 per brand on the first pass.
    const list = [];
    const perBrand = {};
    inBudget.forEach((r) => {
      if (list.length >= count || (perBrand[r.d.brandKey] || 0) >= 2) return;
      perBrand[r.d.brandKey] = (perBrand[r.d.brandKey] || 0) + 1;
      list.push(r);
    });
    inBudget.forEach((r) => { if (list.length < count && !list.includes(r)) list.push(r); });

    // Too few within budget: top up with the closest-priced good matches.
    const minimum = Math.min(3, count);
    if (list.length < minimum) {
      const extra = pool
        .filter((r) => !list.includes(r))
        .sort((x, y) => Math.abs(x.v.price - opts.low) - Math.abs(y.v.price - opts.low) || y.rank - x.rank);
      for (const r of extra) {
        if (list.length >= minimum) break;
        list.push({ ...r, outsideBudget: true });
      }
    }
    return { list, exact: inBudget.length, total: pool.length };
  }

  window.MattressFinderScore = { eligible, derive, score, recommend, sizeKey, firmnessValue, targetFirmness, FIRMNESS_LABELS };

  /* ======================= UI ======================= */

  if (typeof HTMLElement === 'undefined' || !window.customElements || customElements.get('mattress-finder')) return;

  const STEPS = ['people', 'firmness', 'type', 'position', 'temp', 'weight', 'prefs', 'budget', 'size'];
  const STORE_KEY = 'm4u_mattress_finder';
  const TEMP_LABELS = ['Very cool', 'Cool', 'Normal', 'Warm', 'Very hot'];
  const MIN_LOADING_MS = 1800;

  const EXPLAIN = {
    size: {
      single: 'A single is ideal for one adult or child and fits neatly into smaller rooms.',
      double: 'A double is roomy for one and cosy for two — the UK\'s most popular size.',
      kingsize: 'A king size gives couples more room and extra length for taller sleepers.',
      superking: 'A superking is the most spacious size, perfect for couples who like room to stretch out.',
      'small single': 'A small single is great for children\'s rooms, bunk beds and compact spaces.',
      'small double': 'A small double gives one person plenty of space without taking over the room.',
    },
    type: {
      foam: 'Memory foam moulds to your body, spreading your weight and easing pressure points.',
      springs: 'Springs give responsive, breathable support with a classic bouncy feel.',
      hybrid: 'Springs and foam combined — supportive and breathable with pressure-relieving comfort on top.',
      any: 'You didn\'t mind what\'s inside, so we picked the best construction for how you sleep.',
    },
    position: {
      side: 'Side sleepers need a little give at the shoulders and hips to keep the spine straight.',
      back: 'Back sleepers need even support under the lower back to keep the spine aligned.',
      front: 'Front sleepers need a firmer surface so the hips don\'t sink and arch the back.',
      mixed: 'If you move around, a responsive mattress makes it easy to change position.',
    },
  };

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  class MattressFinder extends HTMLElement {
    connectedCallback() {
      this.screens = {};
      this.querySelectorAll('[data-screen]').forEach((el) => { this.screens[el.dataset.screen] = el; });
      this.head = this.querySelector('[data-mf-head]');
      this.backRow = this.querySelector('[data-mf-back-row]');
      this.opts = {
        count: Number(this.dataset.resultsCount) || 6,
        low: Number(this.dataset.budgetLow) || 30000,
        high: Number(this.dataset.budgetHigh) || 50000,
      };
      this.money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' });
      this.answers = this.load();
      this.editing = false;
      this.current = null;

      this.buildStepper();
      this.bind();
      this.syncInputs();

      this.show(this.screenFromUrl() || 'intro', { history: 'replace' });
      window.addEventListener('popstate', () => this.show(this.screenFromUrl() || 'intro', { history: 'none' }));
    }

    /* ---------- state ---------- */

    load() {
      try { return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
    }

    save() {
      try { sessionStorage.setItem(STORE_KEY, JSON.stringify(this.answers)); } catch (e) { /* storage blocked */ }
    }

    answered(key) {
      return Object.prototype.hasOwnProperty.call(this.answers, key);
    }

    firstUnanswered() {
      return STEPS.find((k) => !this.answered(k)) || null;
    }

    screenFromUrl() {
      const step = new URLSearchParams(location.search).get('step');
      if (!step) return null;
      const name = /^\d+$/.test(step) ? STEPS[Number(step) - 1] : step;
      if (!name || !this.screens[name] || name === 'intro' || name === 'loading') return null;
      // Never jump past a question that hasn't been answered yet.
      const gap = this.firstUnanswered();
      if (gap && (name === 'recap' || name === 'results' || STEPS.indexOf(name) > STEPS.indexOf(gap))) return gap;
      return name;
    }

    urlFor(name) {
      const url = new URL(location.href);
      const idx = STEPS.indexOf(name);
      if (name === 'intro') url.searchParams.delete('step');
      else url.searchParams.set('step', idx >= 0 ? String(idx + 1) : name);
      return url.pathname + url.search + url.hash;
    }

    /* ---------- navigation ---------- */

    show(name, { history: mode = 'push' } = {}) {
      const target = this.screens[name];
      if (!target) return;
      Object.entries(this.screens).forEach(([key, el]) => { el.hidden = key !== name; });
      target.classList.remove('is-entering');
      void target.offsetWidth; // restart the fade-in animation
      target.classList.add('is-entering');

      const prev = this.current;
      this.current = name;
      this.advancing = false;
      const isStep = STEPS.includes(name);
      if (isStep) this.loadFeed(); // warm the product feed while they answer
      this.head.hidden = name === 'intro';
      this.classList.toggle('is-results', name === 'results');
      this.backRow.hidden = !isStep && name !== 'recap';
      this.updateStepper();

      if (mode !== 'none' && name !== 'loading') {
        const url = this.urlFor(name);
        if (mode === 'replace') history.replaceState({ mf: name }, '', url);
        else history.pushState({ mf: name }, '', url);
      }

      if (name === 'weight') this.prepareWeight();
      if (name === 'recap') this.renderRecap();
      if (name === 'results' && prev !== 'loading') this.runResults(false);

      if (prev && prev !== name) {
        // Bring the top of the finder back into view, below the theme's sticky header.
        const offset = this.stickyHeaderHeight() + 20;
        const top = this.getBoundingClientRect().top;
        // Instant jump: smooth scrolling is ignored on this theme, which left the tracker under the header.
        if (top < offset) window.scrollTo(0, window.scrollY + top - offset);
        const heading = target.querySelector('.mf-q, h1');
        if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
      }
      this.track(name);
    }

    stickyHeaderHeight() {
      const header = document.querySelector('.cc-header--sticky, sticky-header, .section-header.shopify-section-header-sticky');
      if (!header || !/sticky|fixed/.test(getComputedStyle(header).position)) return 0;
      return header.getBoundingClientRect().height;
    }

    next(fromKey) {
      if (this.editing) {
        // Changing the number of sleepers means the weight answer needs redoing.
        const w = this.answers.weight;
        const needed = this.answers.people === 'two' ? 2 : 1;
        if (fromKey === 'people' && Array.isArray(w) && w.length !== needed) return this.show('weight');
        this.editing = false;
        return this.show('recap');
      }
      const idx = STEPS.indexOf(fromKey);
      this.show(idx < STEPS.length - 1 ? STEPS[idx + 1] : 'recap');
    }

    back() {
      if (this.current === 'recap') return this.show(STEPS[STEPS.length - 1]);
      const idx = STEPS.indexOf(this.current);
      this.show(idx > 0 ? STEPS[idx - 1] : 'intro');
    }

    restart() {
      this.answers = {};
      this.save();
      this.editing = false;
      this.syncInputs();
      this.show('people');
    }

    /* ---------- stepper ---------- */

    buildStepper() {
      const list = this.querySelector('[data-mf-stepper]');
      const check = this.icon('check');
      list.innerHTML = STEPS.map((key, i) => {
        const label = this.screens[key].dataset.label || '';
        return `<li class="mf-stepper__item" data-step-key="${key}">
          <button type="button" class="mf-stepper__dot" data-mf-goto="${key}" aria-label="Step ${i + 1}: ${esc(label)}">
            <span class="mf-stepper__num">${i + 1}</span><span class="mf-stepper__check">${check}</span>
          </button>
          <span class="mf-stepper__label">${esc(label)}</span>
        </li>`;
      }).join('');
      this.stepItems = Array.from(list.children);
    }

    updateStepper() {
      const idx = STEPS.indexOf(this.current);
      this.stepItems.forEach((li, i) => {
        const isCurrent = i === idx;
        const done = !isCurrent && this.answered(STEPS[i]);
        li.classList.toggle('is-done', done);
        li.classList.toggle('is-current', isCurrent);
        const btn = li.querySelector('button');
        btn.disabled = !done;
        if (isCurrent) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
      });
    }

    /* ---------- events ---------- */

    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target;
        const start = t.closest('[data-mf-start]');
        if (start) { this.loadFeed(); return this.show(this.firstUnanswered() || 'recap'); }

        const option = t.closest('.mf-card[data-value], .mf-size[data-value]');
        if (option) return this.choose(option);

        const goto = t.closest('[data-mf-goto]');
        if (goto && !goto.disabled) return this.show(goto.dataset.mfGoto);

        if (t.closest('[data-mf-back]')) return this.back();
        if (t.closest('[data-mf-next]')) return this.commitTemp();
        if (t.closest('[data-mf-weight-next]')) return this.commitWeight();
        if (t.closest('[data-mf-multi-next]')) return this.commitPrefs();
        if (t.closest('[data-mf-show-results]')) return this.runResults(true);
        if (t.closest('[data-mf-restart]')) return this.restart();

        const tab = t.closest('[data-mf-tab]');
        if (tab) return this.selectTab(tab.dataset.mfTab);

        const tempSet = t.closest('[data-mf-temp-set]');
        if (tempSet) { this.tempInput.value = tempSet.dataset.mfTempSet; return this.updateTemp(); }

        const edit = t.closest('[data-mf-edit]');
        if (edit) { this.editing = true; return this.show(edit.dataset.mfEdit); }

        const help = t.closest('[data-mf-help]');
        if (help) return this.openHelp(help.dataset.mfHelp, help);
        if (t.closest('[data-mf-drawer-close]')) return this.closeHelp();
      });

      this.tempInput = this.querySelector('[data-mf-temp]');
      this.tempInput.addEventListener('input', () => this.updateTemp());

      this.weightInputs = Array.from(this.querySelectorAll('[data-mf-weight]'));
      this.weightInputs.forEach((input) => input.addEventListener('input', () => {
        this.weightTouched = true;
        this.updateWeight();
      }));

      this.prefInputs = Array.from(this.querySelectorAll('.mf-check__input'));
      this.prefInputs.forEach((input) => input.addEventListener('change', () => this.updatePrefsButton()));

      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeHelp(); });
      document.addEventListener('wishlist:change', () => this.syncSaveButtons());
    }

    choose(option) {
      if (this.advancing) return; // ignore double clicks while moving on
      this.advancing = true;
      const screen = option.closest('[data-screen]');
      const key = screen.dataset.screen;
      this.answers[key] = option.dataset.value;
      this.save();
      this.markSelected(screen, option.dataset.value);
      setTimeout(() => this.next(key), 220);
    }

    markSelected(screen, value) {
      screen.querySelectorAll('[data-value]').forEach((el) => {
        const on = el.dataset.value === value;
        el.classList.toggle('is-selected', on);
        el.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    selectTab(name) {
      const screen = this.screens.size;
      screen.querySelectorAll('.mf-tab').forEach((b) => {
        const on = b.dataset.mfTab === name;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      screen.querySelectorAll('[data-mf-tabpanel]').forEach((p) => { p.hidden = p.dataset.mfTabpanel !== name; });
    }

    /* ---------- temperature ---------- */

    updateTemp() {
      const v = Number(this.tempInput.value);
      const copy = this.querySelector('[data-mf-temp-copy]').content.children[v];
      this.querySelector('[data-mf-temp-note]').innerHTML = `<strong>${esc(TEMP_LABELS[v])}</strong> — ${esc(copy ? copy.textContent : '')}`;
      this.querySelectorAll('[data-mf-temp-set]').forEach((b) => b.classList.toggle('is-active', Number(b.dataset.mfTempSet) === v));
      this.tempInput.style.setProperty('--fill', `${(v / 4) * 100}%`);
    }

    commitTemp() {
      this.answers.temp = Number(this.tempInput.value);
      this.save();
      this.next('temp');
    }

    /* ---------- weight ---------- */

    prepareWeight() {
      const two = this.answers.people === 'two';
      const q = this.querySelector('[data-mf-weight-q]');
      q.textContent = two ? q.dataset.two : q.dataset.one;
      this.querySelector('[data-mf-weight-card="2"]').hidden = !two;
      const w = this.answers.weight;
      this.weightTouched = Array.isArray(w) && w.length === (two ? 2 : 1);
      this.updateWeight();
    }

    weightText(st) {
      return `${st} Stone / ${Math.round(st * STONE_KG)}kg`;
    }

    updateWeight() {
      this.weightInputs.forEach((input) => {
        const card = input.closest('[data-mf-weight-card]');
        const st = Number(input.value);
        const max = Number(input.max);
        card.querySelector('[data-mf-weight-reading]').textContent = st >= max ? `${st}+ Stone / ${Math.round(st * STONE_KG)}kg+` : this.weightText(st);
        input.style.setProperty('--fill', `${((st - input.min) / (input.max - input.min)) * 100}%`);
      });
      const btn = this.querySelector('[data-mf-weight-next]');
      btn.textContent = this.weightTouched ? btn.dataset.active : btn.dataset.idle;
    }

    commitWeight() {
      const two = this.answers.people === 'two';
      this.answers.weight = this.weightTouched
        ? this.weightInputs.slice(0, two ? 2 : 1).map((i) => Number(i.value))
        : null;
      this.save();
      this.next('weight');
    }

    /* ---------- preferences ---------- */

    updatePrefsButton() {
      const btn = this.querySelector('[data-mf-multi-next]');
      btn.textContent = this.prefInputs.some((i) => i.checked) ? btn.dataset.active : btn.dataset.idle;
    }

    commitPrefs() {
      this.answers.prefs = this.prefInputs.filter((i) => i.checked).map((i) => i.value);
      this.save();
      this.next('prefs');
    }

    /* Put saved answers back into the controls (after refresh / restart). */
    syncInputs() {
      ['people', 'firmness', 'type', 'position', 'budget', 'size'].forEach((key) => {
        this.markSelected(this.screens[key], this.answers[key]);
      });
      const size = this.screens.size.querySelector(`[data-value="${CSS.escape(this.answers.size || '')}"]`);
      this.selectTab(size && size.closest('[data-mf-tabpanel="other"]') ? 'other' : 'popular');

      this.tempInput.value = Number.isInteger(this.answers.temp) ? this.answers.temp : 2;
      this.updateTemp();

      const w = Array.isArray(this.answers.weight) ? this.answers.weight : [];
      this.weightInputs.forEach((input, i) => { input.value = w[i] || input.defaultValue; });
      this.weightTouched = w.length > 0;
      this.updateWeight();

      const prefs = this.answers.prefs || [];
      this.prefInputs.forEach((input) => { input.checked = prefs.includes(input.value); });
      this.updatePrefsButton();
    }

    /* ---------- recap ---------- */

    icon(name) {
      const tpl = this.querySelector('[data-mf-icons]');
      const el = tpl && tpl.content.querySelector(`[data-icon="${name}"]`);
      return el ? el.innerHTML : '';
    }

    recapRow(key) {
      const screen = this.screens[key];
      const a = this.answers;
      const val = a[key];
      const strong = (s) => `<strong>${esc(s)}</strong>`;
      let html = '';
      let icon = screen.dataset.icon ? this.icon(screen.dataset.icon) : '';

      if (key === 'temp') {
        html = `Your sleeping temperature is ${strong(TEMP_LABELS[val] || 'Normal')}`;
      } else if (key === 'weight') {
        if (!Array.isArray(val)) html = `You'd ${strong('prefer not to say')} your weight`;
        else if (val.length === 1) html = `You weigh ${strong(this.weightText(val[0]))}`;
        else html = `Person 1 weighs ${strong(this.weightText(val[0]))} and Person 2 weighs ${strong(this.weightText(val[1]))}`;
      } else if (key === 'prefs') {
        const chosen = this.prefInputs.filter((i) => (val || []).includes(i.value));
        if (!chosen.length) html = screen.dataset.recapEmpty;
        else {
          html = screen.dataset.recap.replace('[value]', strong(chosen.map((i) => i.dataset.recapValue).join(', ')));
          const ico = chosen[0].closest('.mf-check').querySelector('.mf-check__icon');
          if (ico) icon = ico.innerHTML;
        }
      } else {
        const option = screen.querySelector(`[data-value="${CSS.escape(String(val))}"]`);
        if (!option) return '';
        html = option.dataset.recapFull || screen.dataset.recap.replace('[value]', strong(option.dataset.recapValue));
        const art = option.querySelector('.mf-card__art, .mf-size__art');
        if (art) icon = art.innerHTML;
      }
      return `<li class="mf-recap__row">
        <span class="mf-recap__icon">${icon}</span>
        <span class="mf-recap__text">${html}</span>
        <button type="button" class="mf-link mf-recap__edit" data-mf-edit="${key}">Edit</button>
      </li>`;
    }

    renderRecap() {
      this.querySelector('[data-mf-recap-list]').innerHTML = STEPS.map((k) => this.recapRow(k)).join('');
    }

    /* ---------- results ---------- */

    loadFeed() {
      if (this.feedPromise) return this.feedPromise;
      const base = this.dataset.feed;
      const get = async (page) => {
        // No JSON Accept header: Shopify would answer with its built-in collection JSON instead of our template.
        const res = await fetch(`${base}&page=${page}`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`Finder feed HTTP ${res.status}`);
        return res.json();
      };
      this.feedPromise = (async () => {
        const first = await get(1);
        if (!first || !Array.isArray(first.products)) throw new Error('Finder feed returned unexpected data');
        let products = first.products;
        for (let page = 2; page <= Math.min(first.pages || 1, 8); page++) {
          products = products.concat((await get(page)).products || []);
        }
        return products;
      })();
      this.feedPromise.catch(() => { this.feedPromise = null; });
      return this.feedPromise;
    }

    async runResults(withLoader) {
      const gap = this.firstUnanswered();
      if (gap) return this.show(gap);
      const token = (this.runToken = (this.runToken || 0) + 1);
      if (withLoader) this.show('loading');
      const started = Date.now();
      let products;
      try {
        products = await this.loadFeed();
      } catch (e) {
        products = null;
      }
      if (withLoader) await wait(Math.max(0, MIN_LOADING_MS - (Date.now() - started)));
      if (token !== this.runToken) return;

      this.renderResults(products);
      if (this.current !== 'results') this.show('results', { history: withLoader ? 'push' : 'replace' });
    }

    renderResults(products) {
      const listEl = this.querySelector('[data-mf-results-list]');
      const titleEl = this.querySelector('[data-mf-results-title]');
      if (!products) {
        titleEl.textContent = 'Sorry, something went wrong';
        listEl.innerHTML = '<p class="mf-results__error">We couldn\'t load our mattresses just now. Please refresh the page to try again.</p>';
        return;
      }
      const { list, exact } = recommend(products, this.answers, this.opts);
      titleEl.textContent = exact > 0
        ? titleEl.dataset.many.replace('[count]', list.filter((r) => !r.outsideBudget).length)
        : titleEl.dataset.none;

      const tpl = this.querySelector('[data-mf-card-template]');
      listEl.innerHTML = '';
      list.forEach((r, i) => listEl.appendChild(this.buildCard(tpl, r, i)));
      this.syncSaveButtons();
      this.renderExplained(list[0]);
      this.track('results', { results: list.length });
    }

    buildCard(tpl, r, index) {
      const { d, v } = r;
      const node = tpl.content.firstElementChild.cloneNode(true);
      const q = (sel) => node.querySelector(sel);
      const href = `${d.url}${d.url.includes('?') ? '&' : '?'}variant=${v.id}`;

      node.querySelectorAll('[data-r-link]').forEach((a) => { a.href = href; });
      q('[data-r-title]').textContent = d.title;

      // Image gallery with previous / next arrows.
      const images = d.images.length ? d.images : d.image ? [d.image] : [];
      const img = q('[data-r-img]');
      if (images.length) {
        let current = 0;
        img.src = images[0];
        img.alt = d.imageAlt || d.title;
        if (index < 2) img.loading = 'eager';
        if (images.length > 1) {
          q('[data-r-nav]').hidden = false;
          const go = (step) => {
            current = (current + step + images.length) % images.length;
            img.src = images[current];
            new Image().src = images[(current + 1) % images.length]; // warm the next one
          };
          q('[data-r-prev]').addEventListener('click', () => go(-1));
          q('[data-r-next]').addEventListener('click', () => go(1));
        }
      } else img.remove();

      q('[data-r-badge] span').textContent = r.pct >= 95 ? 'A perfect match' : `${r.pct}% match`;
      if (d.badge) {
        q('[data-r-award-img]').src = d.badge;
        q('[data-r-award]').hidden = false;
      }
      if (d.brandLogo) {
        const brand = q('[data-r-brand]');
        brand.src = d.brandLogo;
        brand.alt = d.title.split(' ')[0];
        brand.hidden = false;
      } else q('[data-r-brand]').remove();

      if (d.rating > 0) {
        q('[data-r-stars]').style.width = `${Math.min(100, (d.rating / 5) * 100)}%`;
        q('[data-r-rating-num]').textContent = `${d.rating.toFixed(1)} out of 5`;
        q('[data-r-rating]').setAttribute('aria-label', `Rated ${d.rating.toFixed(1)} out of 5`);
      } else q('[data-r-rating]').remove();

      const tick = this.icon('check-circle');
      const bullets = [...d.bullets.slice(0, 5), `Firmness: ${d.firmLabel}`];
      q('[data-r-bullets]').innerHTML = bullets.map((b) => `<li>${tick}<span>${esc(b)}</span></li>`).join('');

      q('[data-r-price]').textContent = this.formatMoney(v.price);
      if (v.compare > v.price) {
        q('[data-r-was]').textContent = `RRP ${this.formatMoney(v.compare)}`;
        q('[data-r-save]').textContent = `Save ${this.formatMoney(v.compare - v.price)}`;
      } else { q('[data-r-was]').remove(); q('[data-r-save]').remove(); }

      const financeText = this.dataset.financeText;
      const divisor = Number(this.dataset.financeDivisor) || 0;
      if (financeText && divisor > 1) {
        const f = q('[data-r-finance]');
        f.textContent = financeText.replace('[amount]', this.formatMoney(Math.ceil(v.price / divisor), true));
        f.hidden = false;
      }
      if (r.outsideBudget) {
        const note = q('[data-r-note]');
        note.textContent = 'Just outside your budget, but a great match';
        note.hidden = false;
      }

      q('[data-r-save-btn]').dataset.handle = d.handle;
      return node;
    }

    formatMoney(cents, keepPence) {
      const s = this.money.format(cents / 100);
      return keepPence ? s : s.replace(/\.00$/, '');
    }

    syncSaveButtons() {
      const api = window.M4UWishlist;
      this.querySelectorAll('[data-r-save-btn]').forEach((btn) => {
        const on = Boolean(api && api.has(btn.dataset.handle));
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.querySelector('span').textContent = on ? 'Saved' : 'Save';
      });
    }

    renderExplained(top) {
      const a = this.answers;
      const sizeOption = this.screens.size.querySelector(`[data-value="${CSS.escape(a.size || '')}"]`);
      const typeOption = this.screens.type.querySelector(`[data-value="${CSS.escape(a.type || '')}"]`);
      const posOption = this.screens.position.querySelector(`[data-value="${CSS.escape(a.position || '')}"]`);
      const target = targetFirmness(a);
      const rows = [
        ['Mattress size', sizeOption && sizeOption.dataset.recapValue, EXPLAIN.size[sizeKey(a.size)]],
        ['Mattress type', typeOption && (typeOption.dataset.recapValue || 'Any'), EXPLAIN.type[a.type]],
        ['Sleeping position', posOption && posOption.dataset.recapValue.replace(/^./, (c) => c.toUpperCase()), EXPLAIN.position[a.position]],
        ['Firmness', FIRMNESS_LABELS[Math.round(target)], `Based on your answers we aimed for a ${FIRMNESS_LABELS[Math.round(target)].toLowerCase()} feel${top ? ` — your top match is ${top.d.firmLabel.toLowerCase()}` : ''}.`],
      ];
      this.querySelector('[data-mf-explained]').innerHTML = rows
        .filter((r) => r[1] && r[2])
        .map(([label, value, text]) => `<div class="mf-explained__card"><div class="mf-explained__label">${esc(label)}</div><div class="mf-explained__value">${esc(value)}</div><p>${esc(text)}</p></div>`)
        .join('');
    }

    /* ---------- help drawer ---------- */

    openHelp(topic, trigger) {
      const tpl = this.querySelector(`template[data-help="${topic}"]`);
      if (!tpl) return;
      const drawer = this.querySelector('[data-mf-drawer]');
      drawer.querySelector('[data-mf-drawer-title]').textContent = tpl.dataset.title;
      const body = drawer.querySelector('[data-mf-drawer-body]');
      body.innerHTML = '';
      body.appendChild(tpl.content.cloneNode(true));
      this.helpTrigger = trigger;
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.documentElement.classList.add('mf-lock');
      drawer.querySelector('.mf-drawer__panel').focus();
    }

    closeHelp() {
      const drawer = this.querySelector('[data-mf-drawer]');
      if (drawer.hidden) return;
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('mf-lock');
      setTimeout(() => { drawer.hidden = true; }, 250);
      if (this.helpTrigger) this.helpTrigger.focus();
    }

    /* ---------- analytics ---------- */

    track(screen, extra) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push(Object.assign({ event: 'mattress_finder', mf_screen: screen }, extra || {}));
    }
  }

  customElements.define('mattress-finder', MattressFinder);
})();
